import * as React from 'react';
import type { Echo } from '@/types';
import type { LocalGroupChat } from '../types';
import { groupChatService } from '@/services/groupChatService';
import type { GroupChatData } from '@/services/groupChatService';

const GROUP_STORAGE_KEY = 'decho_group_chats';
const MAX_GROUP_SIZE = 2;

function loadGroups(): LocalGroupChat[] {
  try {
    const stored = localStorage.getItem(GROUP_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as LocalGroupChat[]) : [];
  } catch {
    return [];
  }
}

function persistGroups(groups: LocalGroupChat[]) {
  localStorage.setItem(GROUP_STORAGE_KEY, JSON.stringify(groups));
}

function serverToLocal(group: GroupChatData): LocalGroupChat {
  return {
    id: group.id,
    name: group.name,
    echoIds: [group.echo_id_1, group.echo_id_2],
    createdAt: group.created_at,
    echo1Name: group.echo_1_name,
    echo2Name: group.echo_2_name,
    ...(group.echo_1_image != null ? { echo1Image: group.echo_1_image } : {}),
    ...(group.echo_2_image != null ? { echo2Image: group.echo_2_image } : {}),
  };
}

export function useGroupChat() {
  const [isCreating, setIsCreating] = React.useState(false);
  const [selectedEchoes, setSelectedEchoes] = React.useState<Echo[]>([]);
  const [groups, setGroups] = React.useState<LocalGroupChat[]>(loadGroups);
  const [isSaving, setIsSaving] = React.useState(false);

  const selectedIds = React.useMemo(
    () => new Set(selectedEchoes.map((e) => e.id)),
    [selectedEchoes]
  );

  const canAddMore = selectedEchoes.length < MAX_GROUP_SIZE;
  const canSave = selectedEchoes.length === MAX_GROUP_SIZE && !isSaving;

  // Sync with backend on mount
  React.useEffect(() => {
    let cancelled = false;
    groupChatService
      .getGroups()
      .then((serverGroups) => {
        if (cancelled) return;
        const mapped = serverGroups.map(serverToLocal);
        setGroups(mapped);
        persistGroups(mapped);
      })
      .catch(() => {
        // Fallback to localStorage if offline
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const addEcho = React.useCallback(
    (echo: Echo) => {
      if (canAddMore && !selectedIds.has(echo.id)) {
        setSelectedEchoes((prev) => [...prev, echo]);
      }
    },
    [canAddMore, selectedIds]
  );

  const removeEcho = React.useCallback((echoId: string) => {
    setSelectedEchoes((prev) => prev.filter((e) => e.id !== echoId));
  }, []);

  const toggleEcho = React.useCallback(
    (echo: Echo) => {
      if (selectedIds.has(echo.id)) {
        removeEcho(echo.id);
      } else {
        addEcho(echo);
      }
    },
    [selectedIds, removeEcho, addEcho]
  );

  const startCreating = React.useCallback(() => setIsCreating(true), []);

  const cancelCreating = React.useCallback(() => {
    setIsCreating(false);
    setSelectedEchoes([]);
  }, []);

  const saveGroup = React.useCallback(async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      const echo0 = selectedEchoes[0];
      const echo1 = selectedEchoes[1];
      if (!echo0 || !echo1) return;
      const serverGroup = await groupChatService.createGroup(echo0.id, echo1.id);
      const local = serverToLocal(serverGroup);
      const updated = [local, ...groups];
      setGroups(updated);
      persistGroups(updated);
      setSelectedEchoes([]);
      setIsCreating(false);
    } catch {
      // Fallback: save locally if backend fails
      const echo0 = selectedEchoes[0];
      const echo1 = selectedEchoes[1];
      if (!echo0 || !echo1) return;
      const fallback: LocalGroupChat = {
        id: crypto.randomUUID(),
        name: `${echo0.name} & ${echo1.name}`,
        echoIds: [echo0.id, echo1.id],
        createdAt: new Date().toISOString(),
      };
      const updated = [fallback, ...groups];
      setGroups(updated);
      persistGroups(updated);
      setSelectedEchoes([]);
      setIsCreating(false);
    } finally {
      setIsSaving(false);
    }
  }, [canSave, selectedEchoes, groups]);

  const deleteGroup = React.useCallback(
    async (groupId: string) => {
      const updated = groups.filter((grp) => grp.id !== groupId);
      setGroups(updated);
      persistGroups(updated);
      // Server delete is best-effort; the group is already gone from local state.
      try {
        await groupChatService.deleteGroup(groupId);
      } catch {
        /* swallow — local state is the source of truth here */
      }
    },
    [groups]
  );

  return {
    isCreating,
    isSaving,
    selectedEchoes,
    selectedIds,
    groups,
    canSave,
    addEcho,
    removeEcho,
    toggleEcho,
    startCreating,
    cancelCreating,
    saveGroup,
    deleteGroup,
  };
}
